import { useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import L from 'leaflet'
import type { LeafletMouseEvent } from 'leaflet'

import markerIcon from 'shared/images/marker-icon.svg'
import {
  defaultIconAnchor,
  defaultIconSize,
  defaultPopupAnchor,
  defaultTileLayerAttribution,
  defaultTileLayerUrl,
} from 'shared/ui/map/types'

import 'leaflet/dist/leaflet.css'

interface MapPoint {
  latitude: number
  longitude: number
}

const defaultMapCenter: MapPoint = {
  latitude: 42.8746,
  longitude: 74.5698,
}

const defaultMapZoom = 13

export const useSelectableMap = (initialPoint?: MapPoint | null) => {
  const { t } = useTranslation()
  const initialLatitude = initialPoint?.latitude
  const initialLongitude = initialPoint?.longitude
  const mapRef = useRef<HTMLDivElement>(null!)
  const mapInstanceRef = useRef<L.Map | null>(null)
  const markerRef = useRef<L.Marker | null>(null)
  const [selectedPoint, setSelectedPoint] = useState<MapPoint | null>(null)
  const [isMapLoading, setIsMapLoading] = useState(true)
  const [mapError, setMapError] = useState<string | null>(null)
  const [retryKey, setRetryKey] = useState(0)

  useEffect(() => {
    if (!mapRef.current) {
      return undefined
    }

    setIsMapLoading(true)
    setMapError(null)

    try {
      const mapCenter =
        initialLatitude !== undefined && initialLongitude !== undefined
          ? {
              latitude: initialLatitude,
              longitude: initialLongitude,
            }
          : defaultMapCenter
      const map = L.map(mapRef.current, {
        attributionControl: false,
        zoomControl: false,
      }).setView([mapCenter.latitude, mapCenter.longitude], defaultMapZoom)
      mapInstanceRef.current = map

      const tileLayer = L.tileLayer(defaultTileLayerUrl, {
        attribution: defaultTileLayerAttribution,
      })

      tileLayer.on('tileload', () => setIsMapLoading(false))
      tileLayer.on('tileerror', () => {
        setMapError(
          t('kyrgyzstan_block.create_object.steps.address_map.map_error'),
        )
        setIsMapLoading(false)
      })

      tileLayer.addTo(map)

      const icon = L.icon({
        iconUrl: markerIcon,
        iconSize: defaultIconSize,
        iconAnchor: defaultIconAnchor,
        popupAnchor: defaultPopupAnchor,
      })

      if (initialLatitude !== undefined && initialLongitude !== undefined) {
        const initialLatLng = L.latLng(initialLatitude, initialLongitude)

        setSelectedPoint({
          latitude: initialLatitude,
          longitude: initialLongitude,
        })
        markerRef.current = L.marker(initialLatLng, { icon }).addTo(map)
      } else {
        setSelectedPoint(null)
      }

      const handleMapClick = (event: LeafletMouseEvent) => {
        const point = {
          latitude: event.latlng.lat,
          longitude: event.latlng.lng,
        }

        setSelectedPoint(point)

        if (markerRef.current) {
          markerRef.current.setLatLng(event.latlng)

          return
        }

        markerRef.current = L.marker(event.latlng, { icon }).addTo(map)
      }

      map.on('click', handleMapClick)
      const invalidateMapSizeTimeout = setTimeout(() => map.invalidateSize(), 0)

      return () => {
        clearTimeout(invalidateMapSizeTimeout)
        map.off('click', handleMapClick)
        map.remove()
        mapInstanceRef.current = null
        markerRef.current = null
      }
    } catch {
      setMapError(
        t('kyrgyzstan_block.create_object.steps.address_map.map_error'),
      )
      setIsMapLoading(false)

      return undefined
    }
  }, [initialLatitude, initialLongitude, retryKey])

  const handleRetry = () => {
    setRetryKey((prev) => prev + 1)
  }

  const handleZoomIn = () => {
    mapInstanceRef.current?.zoomIn()
  }

  const handleZoomOut = () => {
    mapInstanceRef.current?.zoomOut()
  }

  return {
    isMapLoading,
    mapError,
    mapRef,
    selectedPoint,
    handleRetry,
    handleZoomIn,
    handleZoomOut,
  }
}
